/**
 * Browser-side context for publish-offer and accept-offer audits
 * (complements server IP / User-Agent). Safe to JSON-serialize; avoid secrets.
 */

export type PublisherAuditClientContext = {
  clientCollectedAt: string;
  userAgent: string;
  language: string;
  languages: string[];
  timezone: string;
  timezoneOffsetMinutes: number;
  screen: {
    width: number;
    height: number;
    availWidth: number;
    availHeight: number;
    colorDepth: number;
    pixelRatio: number;
  };
  platform: string;
  hardwareConcurrency: number | null;
  deviceMemoryGb: number | null;
  referrer: string;
  locationHref: string;
  cookieEnabled: boolean;
  doNotTrack: string | null;
  touchSupport: boolean;
  vendor: string;
  maxTouchPoints: number;
};

const emptyScreen = (): PublisherAuditClientContext['screen'] => ({
  width: 0,
  height: 0,
  availWidth: 0,
  availHeight: 0,
  colorDepth: 0,
  pixelRatio: 1,
});

export const collectPublisherAuditContext =
  (): PublisherAuditClientContext => {
    if (typeof window === 'undefined' || typeof navigator === 'undefined') {
      return {
        clientCollectedAt: new Date().toISOString(),
        userAgent: '',
        language: '',
        languages: [],
        timezone: '',
        timezoneOffsetMinutes: 0,
        screen: emptyScreen(),
        platform: '',
        hardwareConcurrency: null,
        deviceMemoryGb: null,
        referrer: '',
        locationHref: '',
        cookieEnabled: false,
        doNotTrack: null,
        touchSupport: false,
        vendor: '',
        maxTouchPoints: 0,
      };
    }

    const nav = navigator;
    const scr = window.screen;
    const navWithMemory = nav as Navigator & { deviceMemory?: number };

    return {
      clientCollectedAt: new Date().toISOString(),
      userAgent: nav.userAgent || '',
      language: nav.language || '',
      languages: nav.languages ? Array.from(nav.languages) : [],
      timezone: (() => {
        try {
          return Intl.DateTimeFormat().resolvedOptions().timeZone || '';
        } catch {
          return '';
        }
      })(),
      timezoneOffsetMinutes: new Date().getTimezoneOffset(),
      screen: scr
        ? {
            width: scr.width,
            height: scr.height,
            availWidth: scr.availWidth,
            availHeight: scr.availHeight,
            colorDepth: scr.colorDepth,
            pixelRatio:
              typeof window.devicePixelRatio === 'number'
                ? window.devicePixelRatio
                : 1,
          }
        : emptyScreen(),
      platform: nav.platform || '',
      hardwareConcurrency:
        typeof nav.hardwareConcurrency === 'number'
          ? nav.hardwareConcurrency
          : null,
      deviceMemoryGb:
        typeof navWithMemory.deviceMemory === 'number'
          ? navWithMemory.deviceMemory
          : null,
      referrer: typeof document !== 'undefined' ? document.referrer || '' : '',
      locationHref: window.location?.href || '',
      cookieEnabled: !!nav.cookieEnabled,
      doNotTrack: nav.doNotTrack != null ? String(nav.doNotTrack) : null,
      touchSupport:
        'ontouchstart' in window || (nav.maxTouchPoints ?? 0) > 0,
      vendor: nav.vendor || '',
      maxTouchPoints: nav.maxTouchPoints ?? 0,
    };
  };
